import ErrorHumanizerError from '../../classes/ErrorHumanizerError'
import { AccountOnchainState } from '../../interfaces/account'
import { Network } from '../../interfaces/network'
import { RPCProvider } from '../../interfaces/provider'
import { BundlerSwitcher } from '../../services/bundlers/bundlerSwitcher'
import { GasSpeeds } from '../../services/bundlers/types'
import { BaseAccount } from '../account/BaseAccount'
import { AccountOp } from '../accountOp/accountOp'
import { SubmittedAccountOp } from '../accountOp/submittedAccountOp'
import { TokenResult } from '../portfolio'
import { ambireEstimateGas } from './ambireEstimation'
import { bundlerEstimate, fetchBundlerGasPrice } from './estimateBundler'
import { Erc4337GasLimits, FullEstimation, FullEstimationSummary } from './interfaces'
import { providerEstimateGas } from './providerEstimateGas'
import { getTokenBalanceTooLowError } from './tokenBalanceCheck'

// get all possible estimation combinations and leave it to the implementation
// to decide which one is relevant depending on the case.
// there are 3 estimations:
// estimateGas(): the rpc method for retrieving gas
// estimateBundler(): ask the 4337 bundler for a gas price
// Estimation.sol: our own implementation
// each has an use case in diff scenarious:
// - EOA: if payment is native, use estimateGas(); otherwise estimateBundler()
// - SA: if ethereum, use Estimation.sol; otherwise estimateBundler()
export async function getEstimation(
  baseAcc: BaseAccount,
  accountState: AccountOnchainState,
  op: AccountOp,
  network: Network,
  provider: RPCProvider,
  feeTokens: TokenResult[],
  nativeToCheck: string[],
  switcher: BundlerSwitcher,
  pendingUserOp?: SubmittedAccountOp
): Promise<FullEstimation> {
  const ambireEstimation = ambireEstimateGas(
    baseAcc,
    accountState,
    op,
    network,
    provider,
    feeTokens,
    nativeToCheck
  )
  let bundlerGasPrices: GasSpeeds | undefined
  const bundlerEstimation = async (): Promise<Erc4337GasLimits | Error | null> => {
    if (!baseAcc.supportsBundlerEstimation() || !network.erc4337.hasBundlerSupport) return null

    const gasPrice = await fetchBundlerGasPrice(baseAcc, network, switcher)
    if (gasPrice instanceof Error) return gasPrice

    const bundlerEstimateResponse = await bundlerEstimate(
      baseAcc,
      accountState,
      op,
      network,
      feeTokens,
      provider,
      gasPrice,
      switcher,
      undefined,
      pendingUserOp
    )
    bundlerGasPrices =
      !bundlerEstimateResponse || bundlerEstimateResponse instanceof Error
        ? gasPrice
        : bundlerEstimateResponse.gasPrice
    return bundlerEstimateResponse
  }
  const providerEstimation = providerEstimateGas(
    baseAcc.getAccount(),
    op,
    provider,
    accountState,
    network,
    feeTokens
  )

  const estimations = await Promise.all([ambireEstimation, bundlerEstimation(), providerEstimation])
  const ambireGas = estimations[0]
  const bundlerGas = estimations[1]
  const providerGas = estimations[2]
  const fullEstimation: FullEstimation = {
    provider: providerGas,
    ambire: ambireGas,
    bundler: bundlerGas,
    flags: {},
    bundlerGasPrices
  }

  const criticalError = baseAcc.getEstimationCriticalError(fullEstimation, op)
  if (criticalError) {
    // Only replace a "reason unknown" error (a token like WPLS reverting with no
    // data): a specific one, e.g. "the transfer amount exceeds your account
    // balance", already tells the user what is wrong.
    const isFallback = !!(criticalError as ErrorHumanizerError).isFallbackMessage
    fullEstimation.criticalError =
      (isFallback && (await getTokenBalanceTooLowError(op, provider))) || criticalError
  }

  let flags = {}
  if (!(ambireGas instanceof Error) && ambireGas) flags = { ...ambireGas.flags }
  if (!(bundlerGas instanceof Error) && bundlerGas) flags = { ...bundlerGas.flags }
  fullEstimation.flags = flags
  return fullEstimation
}

export function getEstimationSummary(estimation: FullEstimation): FullEstimationSummary {
  return {
    providerEstimation:
      estimation.provider && !(estimation.provider instanceof Error)
        ? estimation.provider
        : undefined,
    ambireEstimation:
      estimation.ambire && !(estimation.ambire instanceof Error) ? estimation.ambire : undefined,
    bundlerEstimation:
      estimation.bundler && !(estimation.bundler instanceof Error) ? estimation.bundler : undefined,
    flags: estimation.flags,
    bundlerGasPrices: estimation.bundlerGasPrices,
    updatedAt: Date.now()
  }
}
